import React, { useContext, useEffect  } from 'react';
import styled from 'styled-components';
import { FormContext } from '../../../../contexts/generated-content/content-form-context';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../../../stores/types/types';
import ThemeCard from './theme-card';
import { loadThemes, setCreatingTheme, setEditingTheme, setThemeFilter } from '../../../../stores/actions/themes';
import { Theme } from '../../../../stores/types/themes';
import EditThemeTab from './edit-theme-tab';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import AddThemeSplash from './add-theme-splash';
import { THEME_FILTER } from '../../../../stores/types/themes';
import { Virtuoso } from 'react-virtuoso';
import { setModalState } from '../../../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../../../stores/reducers/modal';
import { IconPlusOutline } from '@brandlive/pixel';

const FormContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	
	
	height: 100%;
	max-height: 100%;
	
	overflow-x: hidden;
	overflow-y: hidden;
	
	position: static;

	z-index: $z-index-media-link;
`;

const fakeTheme: Theme = {id: 0, colors: [], version: 1, stylepack: '', name: '', title: ''}

interface Props {
	isForShotEditorModal?: boolean;
}

export default function ThemeTab({ isForShotEditorModal = false }: Props) {
	const defaultThemes = useSelector((s: GS) => s.ThemesReducer.defaultThemes);
	const customThemes = useSelector((s: GS) => s.ThemesReducer.customThemes);
	const themeFilter = useSelector((s: GS) => s.ThemesReducer.themeFilter);
	const channel = useSelector((s: GS) => s.SessionReducer.meeting?.channel);

	const dispatch = useDispatch();

	const scrollRef = React.useRef(null);

	const editTheme = (theme: Theme) => {
		dispatch(setEditingTheme(theme.id, false));
	}

	const {currTemplate, uniqueId, currrentThemeId, applyThemeToItem} = useContext(FormContext);

	const filteredDefaultThemes = React.useMemo(() => {
		if (!currTemplate.stylePack) return [];
		return defaultThemes.filter((t) => t.stylepack === currTemplate.name)
	}, [currTemplate.stylePack, defaultThemes]);

	const renderedThemes: Theme[] = React.useMemo(() => {
		const nThemes = themeFilter === THEME_FILTER.SUGGESTED ? filteredDefaultThemes : customThemes || []
		return [fakeTheme, ...nThemes, fakeTheme]
	}, [themeFilter, filteredDefaultThemes, customThemes])

	const addNew = () => {
		dispatch(setCreatingTheme(currTemplate.name, uniqueId));
	}

	const onDelete = React.useCallback((theme: Theme) => {
		if (!channel) return console.error('No channel when deleting style');
		dispatch(setModalState({
			modalData: {channel, theme},
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.DELETE_THEME
		}))
	}, [channel])

	useEffect(() => {
		if (currTemplate.name && channel) {
			dispatch(loadThemes(channel));
		}
	}, [currTemplate.name, channel])
	
	return (
		<FormContainer>
			<div className='theme-tab-button-container'>
				<div
					className={classNames('button-gradient-btn bl-font-l2', {selected: themeFilter === THEME_FILTER.SUGGESTED})}
					onClick={() => dispatch(setThemeFilter(THEME_FILTER.SUGGESTED))}
				>Suggested</div>
				<div
					className={classNames('button-gradient-btn bl-font-l2', {selected: themeFilter === THEME_FILTER.CUSTOM})}
					onClick={() => dispatch(setThemeFilter(THEME_FILTER.CUSTOM))}
				>Your Styles</div>
				<div className='top-scroll-gradient'/>
			</div>
			<div className='theme-tab-theme-list custom-scrollbar' ref={scrollRef}>
				<Virtuoso
					data={renderedThemes}
					firstItemIndex={renderedThemes[0].id}
					initialTopMostItemIndex={0}
					initialScrollTop={0}
					fixedItemHeight={64}
					increaseViewportBy={200}
					// startReached={loadMoreMessages}
					style={{ height: '100%', overflowY: 'scroll'}}
					context={scrollRef}
					itemContent={(i, t: Theme) => t.title === ''  
						? <div key={`${t.id} + ${i}`} style={{minHeight: '20px', height: '20px'}}/>
						: <ThemeCard 
							key={`${t.id} + ${i}`}
							theme={t}
							onEdit={editTheme}
							onDelete={onDelete}
							onSelect={applyThemeToItem}
							isSelected={currrentThemeId === t.id}
							uniqueId={uniqueId}
							isForShotEditorModal={isForShotEditorModal}
						/>
					}
				/>
				{!isForShotEditorModal && (
					<>
						{(themeFilter === THEME_FILTER.CUSTOM && customThemes.length === 0)
							? <AddThemeSplash stylepack={currTemplate.name} currItemUniqueId={uniqueId} addNew={addNew}/>
							: <div className='add-theme-card-container'>
								<div className='bottom-scroll-gradient'/>
								<button className='theme-card add-theme-card flex-center' onClick={addNew}>
									<IconPlusOutline color="var(--local-bttn-sec-default-icotext" height="16" width="16"/>
									<div className='add-new-theme-text bl-font-s1 margin-left-small'>Add new</div>
								</button>
							</div>
						}
					</>
				)}
			</div>
			<EditThemeTab />
		</FormContainer>
	);
}
